import { Box, Typography } from '@mui/material';
import { IconPhoto } from '@tabler/icons-react';
import PropTypes from 'prop-types';

/**
 * ImagePlaceholder - Reusable placeholder component for stock images
 *
 * Displays a styled placeholder box that indicates where an image will be inserted.
 * Use this component to reserve space for Adobe Stock photos or other images.
 *
 * @param {Object} props - Component props
 * @param {string|number} [props.width] - Width of the placeholder (default: '100%')
 * @param {string|number} [props.height] - Height of the placeholder (default: 200)
 * @param {string} [props.label] - Label text (default: 'Image Placeholder')
 * @param {string} [props.dimensions] - Optional dimensions text (e.g., '800 x 600')
 * @param {string} [props.aspectRatio] - CSS aspect ratio (e.g., '16/9', '4/3')
 * @param {Object} [props.sx] - Additional MUI sx styling props
 * @returns {JSX.Element} Placeholder component
 *
 * @example
 * // Basic usage
 * <ImagePlaceholder />
 *
 * @example
 * // With custom dimensions
 * <ImagePlaceholder
 *   width="100%"
 *   height={300}
 *   label="Hero Image"
 *   aspectRatio="16/9"
 * />
 *
 * @example
 * // With dimension text
 * <ImagePlaceholder
 *   label="Team Photo"
 *   dimensions="1200 x 800"
 * />
 */
function ImagePlaceholder({
  width = '100%',
  height = 200,
  label = 'Image Placeholder',
  dimensions,
  aspectRatio,
  sx = {},
}) {
  return (
    <Box
      sx={{
        width,
        height: aspectRatio ? 'auto' : height,
        aspectRatio: aspectRatio || 'auto',
        minHeight: aspectRatio ? 150 : height,
        backgroundColor: '#f5f5f5',
        border: '2px dashed #ccc',
        borderRadius: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        color: '#999',
        position: 'relative',
        overflow: 'hidden',
        ...sx,
      }}
    >
      <IconPhoto size={48} stroke={1.5} color="#bbb" />
      <Typography
        variant="body2"
        sx={{
          color: '#888',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.75rem',
        }}
      >
        {label}
      </Typography>
      {dimensions && (
        <Typography
          variant="caption"
          sx={{
            color: '#aaa',
            fontSize: '0.7rem',
          }}
        >
          {dimensions}
        </Typography>
      )}
    </Box>
  );
}

ImagePlaceholder.propTypes = {
  /** Width of the placeholder */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Height of the placeholder (ignored if aspectRatio is set) */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Label text displayed in the placeholder */
  label: PropTypes.string,
  /** Optional dimensions text (e.g., '800 x 600') */
  dimensions: PropTypes.string,
  /** CSS aspect ratio (e.g., '16/9', '4/3') - overrides height if set */
  aspectRatio: PropTypes.string,
  /** Additional MUI sx styling props */
  sx: PropTypes.object,
};

export default ImagePlaceholder;
